import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ArrowLeft } from 'lucide-react';
import { useLayout } from '../../contexts/layout-context';
import { Button } from '../ui/button';
import { DashboardMenubar } from '../menubar/menubar';
import { useSelector } from 'react-redux';
import { getUserStateActiveClient } from 'src/redux/user/user.selectors';

export interface NavbarTab {
  id: string;
  label: string;
  key: string;
  onClick?: () => void;
  badge?: number;
  /** When set to ``alert``, show a red indicator icon instead of the badge count. */
  badgeVariant?: 'count' | 'alert' | 'dot';
}

function NavbarDotBadge({ title }: { title: string }) {
  return (
    <span
      className="inline-block size-2 shrink-0 rounded-full bg-[#DC2626]"
      title={title}
      aria-label={title}
    />
  );
}

/** Filled red circle with a geometric exclamation mark (avoids font metric offset). */
function NavbarAlertBadge({ title }: { title: string }) {
  return (
    <span
      className="inline-flex size-[10.625px] shrink-0"
      title={title}
      aria-label={title}
    >
      <svg
        width="10.625"
        height="10.625"
        viewBox="0 0 20 20"
        className="size-[10.625px]"
        aria-hidden
      >
        <circle cx="10" cy="10" r="10" fill="#DC2626" />
        <rect x="9" y="4.5" width="2" height="7.5" rx="1" fill="#FFFFFF" />
        <circle cx="10" cy="15.25" r="1.25" fill="#FFFFFF" />
      </svg>
    </span>
  );
}

interface AppNavbarProps {
  showBackButton?: boolean;
  backPath?: string;
  tabs?: NavbarTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  children?: React.ReactNode;
}

/** Stable default so `tabs` dependency is not a new `[]` every render when omitted. */
const EMPTY_NAVBAR_TABS: NavbarTab[] = [];

function getTabDotBadgeTitle(tabId: string): string {
  if (tabId === 'etl') {
    return 'Recent ETL failures';
  }
  if (tabId === 'campaigns') {
    return 'New requests';
  }
  return 'Attention required';
}

export const AppNavbar: React.FC<AppNavbarProps> = ({
  showBackButton = false,
  backPath,
  tabs: tabsProp = EMPTY_NAVBAR_TABS,
  activeTab,
  onTabChange,
  children,
}) => {
  const navigate = useNavigate();
  const { isMobile, mobileSidebarOpen, setMobileSidebarOpen, sidebarCollapsed } = useLayout();
  const activeClient = useSelector(getUserStateActiveClient);

  const filteredTabs = useMemo(() => {
    if (activeClient) {
      return tabsProp.filter((tab) =>
        activeClient.permissions.some((permission) => permission.tab === tab.key || tab.key === 'ALL')
      );
    }
    return tabsProp.filter((tab) => tab.key !== 'NEVER');
  }, [activeClient, tabsProp]);

  const handleBackClick = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  const handleMenuClick = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  return (
    <nav
      className={`fixed top-0 right-0 h-16 bg-white border-b border-gray-200 z-30 transition-all duration-300 ${
        isMobile ? 'left-0' : sidebarCollapsed ? 'left-16' : 'left-80'
      }`}
    >
      <div className="flex items-center justify-between h-full px-4">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleMenuClick}
              className="h-8 w-8"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {/* Back button */}
          {showBackButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackClick}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}

          {/* Tabs */}
          {filteredTabs.length > 0 && (
            <div className="flex items-center">
              <div className="flex items-center space-x-8">
                {filteredTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`relative px-1 py-2 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => {
                      tab.onClick?.();
                      onTabChange?.(tab.id);
                    }}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {tab.label}
                      {tab.badge != null && tab.badge > 0 ? (
                        tab.badgeVariant === 'alert' ? (
                          <NavbarAlertBadge
                            title={
                              tab.id === 'etl'
                                ? 'Recent ETL failures'
                                : tab.id === 'campaigns'
                                  ? 'New requests'
                                  : 'Recent failures'
                            }
                          />
                        ) : tab.badgeVariant === 'dot' ? (
                          <NavbarDotBadge title={getTabDotBadgeTitle(tab.id)} />
                        ) : (
                          <span
                            className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white tabular-nums"
                            title={
                              tab.id === 'etl'
                                ? 'Recent ETL failures'
                                : 'Recent scheduler failures'
                            }
                          >
                            {tab.badge > 99 ? '99+' : tab.badge}
                          </span>
                        )
                      ) : null}
                    </span>
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom content */}
          {children}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          <DashboardMenubar />
        </div>
      </div>
    </nav>
  );
};